import { useState } from "react"
import type { FormEvent } from "react"

import { withHoneypot } from "@/lib/honeypot"
import { normalizeEmailAddress } from "@/lib/utils"

/** The "name/email/message → POST → idle/loading/ok/error state machine"
 * shared by the small marketing intake forms. Each page keeps its own field
 * set and markup; only the submit/validate/fetch shape is centralized here. */
export function useInquiryForm({
  endpoint,
  genericErrorCopy,
}: {
  endpoint: string
  genericErrorCopy: string
}) {
  const [state, setState] = useState<"idle" | "loading" | "ok" | "error">("idle")
  const [error, setError] = useState("")

  async function submit(
    e: FormEvent,
    email: string,
    buildPayload: (trimmedEmail: string) => Record<string, unknown>,
  ) {
    e.preventDefault()
    if (state === "loading") return

    const trimmedEmail = normalizeEmailAddress(email)
    if (!trimmedEmail) {
      setError("Enter a valid email address.")
      setState("error")
      return
    }

    setState("loading")
    setError("")

    try {
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(withHoneypot(buildPayload(trimmedEmail), e.currentTarget)),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        setError((data as { error?: string })?.error || genericErrorCopy)
        setState("error")
        return
      }
      setState("ok")
    } catch {
      setError("Network error. Try again.")
      setState("error")
    }
  }

  return { state, error, submit }
}
